import { supabase } from './supabase';

export interface TaskTemplateSubtask {
  id: string;
  template_id: string;
  title: string;
  description?: string;
  due_days_offset_from_parent: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface TaskTemplate {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  due_days_offset: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  auto_trigger_stage_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  pipeline_stage?: {
    id: string;
    name: string;
    color: string;
  };
  subtasks?: TaskTemplateSubtask[];
}

export interface Task {
  id: string;
  opportunity_id: string;
  template_id?: string;
  assigned_to?: string;
  title: string;
  description?: string;
  due_date: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  completed_at?: string;
  completed_by?: string;
  notes?: string;
  parent_task_id?: string;
  order_index?: number;
  created_at: string;
  updated_at: string;
  template?: TaskTemplate;
  assigned_user?: {
    id: string;
    email: string;
    full_name: string;
  };
  subtasks?: Task[];
}

export interface CreateTaskTemplateInput {
  brand_id: string;
  name: string;
  description?: string;
  due_days_offset: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  auto_trigger_stage_id?: string;
  is_active?: boolean;
}

export interface CreateTaskInput {
  opportunity_id: string;
  template_id?: string;
  assigned_to?: string;
  title: string;
  description?: string;
  due_date: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  parent_task_id?: string;
  order_index?: number;
}

export const taskService = {
  async getTaskTemplates(brandId: string): Promise<TaskTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('task_templates')
        .select(`
          *,
          pipeline_stage:pipeline_stages(id, name, color),
          subtasks:task_template_subtasks(*)
        `)
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;

      const templates = data || [];

      for (const template of templates) {
        if (template.subtasks) {
          template.subtasks.sort((a: TaskTemplateSubtask, b: TaskTemplateSubtask) =>
            a.order_index - b.order_index
          );
        }
      }

      return templates;
    } catch (error) {
      console.error('Error fetching task templates:', error);
      return [];
    }
  },

  async getActiveTaskTemplates(brandId: string): Promise<TaskTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('task_templates')
        .select(`
          *,
          pipeline_stage:pipeline_stages(id, name, color),
          subtasks:task_template_subtasks(*)
        `)
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      const templates = data || [];

      for (const template of templates) {
        if (template.subtasks) {
          template.subtasks.sort((a: TaskTemplateSubtask, b: TaskTemplateSubtask) =>
            a.order_index - b.order_index
          );
        }
      }

      return templates;
    } catch (error) {
      console.error('Error fetching active task templates:', error);
      return [];
    }
  },

  async createTaskTemplate(input: CreateTaskTemplateInput): Promise<TaskTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('task_templates')
        .insert({
          ...input,
          is_active: input.is_active !== undefined ? input.is_active : true,
        })
        .select(`
          *,
          pipeline_stage:pipeline_stages(id, name, color)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating task template:', error);
      return null;
    }
  },

  async updateTaskTemplate(templateId: string, updates: Partial<CreateTaskTemplateInput>): Promise<TaskTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('task_templates')
        .update(updates)
        .eq('id', templateId)
        .select(`
          *,
          pipeline_stage:pipeline_stages(id, name, color)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating task template:', error);
      return null;
    }
  },

  async deleteTaskTemplate(templateId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('task_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting task template:', error);
      return false;
    }
  },

  async getTasks(opportunityId: string): Promise<Task[]> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          template:task_templates(id, name),
          assigned_user:users!tasks_assigned_to_fkey(id, email, full_name)
        `)
        .eq('opportunity_id', opportunityId)
        .is('parent_task_id', null)
        .order('due_date', { ascending: true });

      if (error) throw error;

      const tasks = data || [];

      for (const task of tasks) {
        const subtasks = await this.getSubtasks(task.id);
        task.subtasks = subtasks;
      }

      return tasks;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  },

  async getSubtasks(parentTaskId: string): Promise<Task[]> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          template:task_templates(id, name),
          assigned_user:users!tasks_assigned_to_fkey(id, email, full_name)
        `)
        .eq('parent_task_id', parentTaskId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      return [];
    }
  },

  async getTasksByBrand(brandId: string, filters?: {
    status?: string[];
    assigned_to?: string;
    overdue?: boolean;
  }): Promise<Task[]> {
    try {
      let query = supabase
        .from('tasks')
        .select(`
          *,
          template:task_templates(id, name),
          assigned_user:users!tasks_assigned_to_fkey(id, email, full_name),
          opportunity:opportunities!inner(id, client_name, brand_id)
        `)
        .eq('opportunity.brand_id', brandId);

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      if (filters?.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }

      if (filters?.overdue) {
        query = query
          .lt('due_date', new Date().toISOString().split('T')[0])
          .in('status', ['pending', 'in_progress']);
      }

      const { data, error } = await query.order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching tasks by brand:', error);
      return [];
    }
  },

  async createTask(input: CreateTaskInput): Promise<Task | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...input,
          assigned_to: input.assigned_to || user?.id || null,
          status: 'pending',
        })
        .select(`
          *,
          template:task_templates(id, name),
          assigned_user:users!tasks_assigned_to_fkey(id, email, full_name)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating task:', error);
      return null;
    }
  },

  async createTaskFromTemplate(opportunityId: string, templateId: string, assignedTo?: string): Promise<Task | null> {
    try {
      const { data: template, error: templateError } = await supabase
        .from('task_templates')
        .select(`
          *,
          subtasks:task_template_subtasks(*)
        `)
        .eq('id', templateId)
        .single();

      if (templateError || !template) {
        throw new Error('Template not found');
      }

      const { data: { user } } = await supabase.auth.getUser();
      const assignedUserId = assignedTo || user?.id || null;

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + template.due_days_offset);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      if (!template.subtasks || template.subtasks.length === 0) {
        return await this.createTask({
          opportunity_id: opportunityId,
          template_id: templateId,
          assigned_to: assignedUserId,
          title: template.name,
          description: template.description,
          due_date: dueDateStr,
          priority: template.priority,
        });
      }

      const sortedSubtasks = [...template.subtasks].sort((a: TaskTemplateSubtask, b: TaskTemplateSubtask) =>
        a.order_index - b.order_index
      );

      const subtasksData = sortedSubtasks.map((subtaskTemplate) => {
        const subtaskDueDate = new Date(dueDate);
        subtaskDueDate.setDate(subtaskDueDate.getDate() + subtaskTemplate.due_days_offset_from_parent);

        return {
          title: subtaskTemplate.title,
          description: subtaskTemplate.description || null,
          due_date: subtaskDueDate.toISOString().split('T')[0],
          priority: subtaskTemplate.priority,
          order_index: subtaskTemplate.order_index,
        };
      });

      const { data, error } = await supabase.rpc('create_task_with_subtasks', {
        p_opportunity_id: opportunityId,
        p_template_id: templateId,
        p_assigned_to: assignedUserId,
        p_title: template.name,
        p_description: template.description || null,
        p_due_date: dueDateStr,
        p_priority: template.priority,
        p_subtasks: subtasksData,
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('Failed to create task');
      }

      const parentTask = data.find((t: Task) => !t.parent_task_id);
      const subtasks = data.filter((t: Task) => t.parent_task_id);

      if (parentTask) {
        parentTask.subtasks = subtasks;
      }

      return parentTask || null;
    } catch (error) {
      console.error('Error creating task from template:', error);
      return null;
    }
  },

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task | null> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId)
        .select(`
          *,
          template:task_templates(id, name),
          assigned_user:users!tasks_assigned_to_fkey(id, email, full_name)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating task:', error);
      return null;
    }
  },

  async completeTask(taskId: string, notes?: string): Promise<Task | null> {
    return await this.updateTask(taskId, {
      status: 'completed',
      notes,
    });
  },

  async reopenTask(taskId: string): Promise<Task | null> {
    return await this.updateTask(taskId, {
      status: 'pending',
      completed_at: undefined,
      completed_by: undefined,
    });
  },

  async deleteTask(taskId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      return false;
    }
  },

  async getOverdueTasks(brandId: string): Promise<Task[]> {
    return await this.getTasksByBrand(brandId, {
      overdue: true,
    });
  },

  async getTaskStats(brandId: string): Promise<{
    total: number;
    pending: number;
    in_progress: number;
    completed: number;
    overdue: number;
  }> {
    try {
      const tasks = await this.getTasksByBrand(brandId, {
        status: ['pending', 'in_progress', 'completed'],
      });

      const today = new Date().toISOString().split('T')[0];

      return {
        total: tasks.length,
        pending: tasks.filter(t => t.status === 'pending').length,
        in_progress: tasks.filter(t => t.status === 'in_progress').length,
        completed: tasks.filter(t => t.status === 'completed').length,
        overdue: tasks.filter(t =>
          t.due_date < today &&
          (t.status === 'pending' || t.status === 'in_progress')
        ).length,
      };
    } catch (error) {
      console.error('Error getting task stats:', error);
      return {
        total: 0,
        pending: 0,
        in_progress: 0,
        completed: 0,
        overdue: 0,
      };
    }
  },

  async createTemplateSubtask(subtask: {
    template_id: string;
    title: string;
    description?: string;
    due_days_offset_from_parent: number;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    order_index?: number;
  }): Promise<TaskTemplateSubtask | null> {
    try {
      const { data, error } = await supabase
        .from('task_template_subtasks')
        .insert({
          ...subtask,
          order_index: subtask.order_index ?? 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating template subtask:', error);
      return null;
    }
  },

  async updateTemplateSubtask(
    subtaskId: string,
    updates: Partial<TaskTemplateSubtask>
  ): Promise<TaskTemplateSubtask | null> {
    try {
      const { data, error } = await supabase
        .from('task_template_subtasks')
        .update(updates)
        .eq('id', subtaskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating template subtask:', error);
      return null;
    }
  },

  async deleteTemplateSubtask(subtaskId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('task_template_subtasks')
        .delete()
        .eq('id', subtaskId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting template subtask:', error);
      return false;
    }
  },
};
